import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Select } from '@aline/common';
import { link } from '@aline/routes';
import { Skeleton, Typography } from '@mui/material';
import { StopCircle } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import { useLocalStorage } from 'react-use';
import { AlineAlert, AlineButton } from 'src/components/generic';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';
import { Apollo } from 'src/gql/generated';
import { useGetPlaybook, useOverrideGlobalCursor } from 'src/hooks';
import { useTabContext } from 'src/layouts/tabs';
import { makeStyles } from 'tss-react/mui';

import { PlaybookHeader } from './PlaybookHeader';
import { RuleDetailsPanel } from './RuleDetailsPanel';
import { RulesSection } from './RulesSection';
import {
  MoveArrowOverlay,
  SuggestionReviewProvider,
  SuggestionsBar,
  useSuggestionReviewState,
} from './suggested-edits';
import type { RuleNavigationMode, SuggestionStop } from './suggested-edits';
import type { PlaybookDetail, PlaybookDetailRule } from './types';
import type { PlaybookGenerationProgress } from './use-playbook-generation-progress';
import {
  getIsGenerationInFlight,
  usePlaybookGenerationProgress,
} from './use-playbook-generation-progress';
import { usePlaybookLiveUpdates } from './use-playbook-live-updates';

/** The narrowest either column may be dragged to, so neither collapses into a strip. */
const DETAILS_MIN_WIDTH_PX = 360;
const EDITOR_MIN_WIDTH_PX = 400;
/** The grab area over the divider: wider than the 1px line so it is easy to catch. */
const DIVIDER_GRAB_WIDTH_PX = 8;
const DETAILS_RATIO_STORAGE_KEY = 'playbook-detail-details-ratio';
/**
 * How far right of a rule row the vertical run of a rule move's arrow sits.
 * A row ends 16px inside its category card, so this puts the run about 14px
 * outside the card's border, in the column's 24px padding, rather than in the
 * narrow gap between the rows and the border.
 */
const RULE_MOVE_ARROW_OUTSET_PX = 30;

const useStyles = makeStyles<{ dragging: boolean }>()((theme, { dragging }) => ({
  // The two columns; the suggestions bar sits above them, spanning both.
  container: {
    display: 'flex',
    flexGrow: 1,
    minHeight: 0,
    // No text gets selected by a drag that overshoots the handle.
    userSelect: dragging ? 'none' : undefined,
  },
  /**
   * Owns the divider line so it spans the full column height, not just the
   * panel's content, and stays put while the panel scrolls inside it. The
   * line turns the app blue while it is being dragged, like the drawer's.
   * Until the user drags it the column takes half the page; a dragged share
   * is set inline in its place. The min and max widths keep both columns at
   * their minimums at whatever width the page has now, since a share saved on
   * a wide window may not fit a narrower one. When the page is too narrow for
   * both, the min width wins and the details column keeps its minimum.
   */
  detailsColumn: {
    borderLeft: `1px solid ${dragging ? theme.appColors.primary : theme.appColors.rule}`,
    // A column so the panel inside can stretch to the full height, making the
    // space below the last position part of the panel's hover area.
    display: 'flex',
    flexBasis: '50%',
    flexDirection: 'column',
    flexShrink: 0,
    maxWidth: `calc(100% - ${EDITOR_MIN_WIDTH_PX}px)`,
    minWidth: DETAILS_MIN_WIDTH_PX,
    overflowY: 'auto',
  },
  /**
   * The grab area for resizing the columns, centred on the divider line
   * without taking layout space: a negative margin on each side pulls the
   * columns back under it. Above the columns so it always catches the pointer.
   */
  divider: {
    cursor: 'col-resize',
    flexShrink: 0,
    margin: `0 -${DIVIDER_GRAB_WIDTH_PX / 2}px`,
    position: 'relative',
    touchAction: 'none',
    width: DIVIDER_GRAB_WIDTH_PX,
    zIndex: 1,
  },
  editorColumn: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: theme.spacing(3),
    minWidth: 0,
    overflowY: 'auto',
    padding: theme.spacing(2.5, 3, 4),
    // The move arrows are positioned against the scrolling column.
    position: 'relative',
  },
  generationAlert: {
    '& .MuiAlert-action': {
      alignItems: 'center',
      marginRight: 0,
    },
    '& .MuiAlert-icon': {
      alignItems: 'center',
      padding: 0,
    },
  },
  message: {
    padding: theme.spacing(3),
  },
  /**
   * The details column while no rule is open. It fills the column, so the
   * column keeps its width and the rules on the left do not jump when a rule
   * opens or closes, and its light gray covers the whole panel so the panel
   * reads as idle beside the white rules column.
   */
  noRule: {
    alignItems: 'center',
    backgroundColor: theme.appColors.background,
    color: theme.appColors.secondaryType,
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'center',
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  page: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
  },
  skeletonCard: {
    borderRadius: 8,
  },
  skeletonHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  skeletonRules: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  skeletonTitleRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

/** The open rule's details, or a placeholder while no rule is open. */
const RuleDetailsColumn = React.memo(function RuleDetailsColumn({
  disabled,
  playbookId,
  rule,
}: {
  disabled: boolean;
  playbookId: string;
  rule: PlaybookDetailRule | null;
}): JSX.Element {
  const { classes } = useStyles({ dragging: false });

  if (!rule) {
    return (
      <Typography
        className={classes.noRule}
        data-cy={Select.playbooks.ruleDetailsEmpty}
        variant="body2"
      >
        Select a rule on the left to see its details here.
      </Typography>
    );
  }

  /**
   * Keyed so selecting another rule remounts the panel. Otherwise the
   * autofocus on an empty rule only fires the first time it opens.
   */
  return <RuleDetailsPanel key={rule.id} disabled={disabled} playbookId={playbookId} rule={rule} />;
});

/**
 * The page once the playbook has arrived. Split from the loader so the review
 * state hook always has a playbook to build its model from.
 */
const LoadedPlaybookDetail = React.memo(function LoadedPlaybookDetail({
  isGenerating,
  onRuleChange,
  onStopped,
  playbook,
  urlRuleId,
}: {
  isGenerating: boolean;
  onRuleChange: (ruleId: UUID | null, mode: RuleNavigationMode) => void;
  onStopped: () => void;
  playbook: PlaybookDetail;
  urlRuleId: UUID | null;
}): JSX.Element {
  const editorColumnRef = useRef<HTMLDivElement | null>(null);
  const detailsColumnRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { detailsRatio, dividerProps, dragging } = useColumnResize(containerRef);
  const { classes } = useStyles({ dragging });
  const review = useSuggestionReviewState({ onRuleChange, playbook, urlRuleId });
  const { currentStop, openRule, selectRule, selectedRuleId } = review;
  useScrollToCurrentStop({ currentStop, detailsColumnRef, editorColumnRef });
  const generationJob = playbook.generationJob ?? null;
  const selectedRule =
    playbook.categories
      .flatMap(category => category.rules)
      .find(rule => rule.id === selectedRuleId) ?? null;

  return (
    <SuggestionReviewProvider review={review}>
      <div className={classes.page} data-cy={Select.playbooks.detailPage}>
        <Helmet>
          <title>{playbook.title || 'Untitled playbook'}</title>
        </Helmet>
        <SuggestionsBar playbookId={playbook.id} />
        <div ref={containerRef} className={classes.container}>
          <div ref={editorColumnRef} className={classes.editorColumn}>
            {isGenerating && generationJob && (
              <GeneratingBanner generationJob={generationJob} onStopped={onStopped} />
            )}
            {Boolean(generationJob?.failedAt) && (
              <GenerationFailedBanner failedReason={generationJob?.failedReason ?? null} />
            )}
            <PlaybookHeader disabled={isGenerating} playbook={playbook} />
            <RulesSection
              disabled={isGenerating}
              isGenerating={isGenerating}
              openCategoryId={generationJob?.openCategory?.id ?? null}
              playbook={playbook}
              selectedRuleId={selectedRuleId}
              onOpenRule={openRule}
              onSelectRule={selectRule}
            />
            <MoveArrowOverlay
              activeMoveId={review.activeMoveId}
              containerRef={editorColumnRef}
              outsetPx={RULE_MOVE_ARROW_OUTSET_PX}
            />
          </div>
          <div
            className={classes.divider}
            data-cy={Select.playbooks.columnDivider}
            role="separator"
            {...dividerProps}
          />
          {/* Always rendered, with a placeholder while no rule is open, so the
              rules column keeps its width across selection changes. */}
          <div
            ref={detailsColumnRef}
            className={classes.detailsColumn}
            style={detailsRatio === undefined ? undefined : { flexBasis: `${detailsRatio * 100}%` }}
          >
            <RuleDetailsColumn
              disabled={isGenerating}
              playbookId={playbook.id}
              rule={selectedRule}
            />
          </div>
        </div>
      </div>
    </SuggestionReviewProvider>
  );
});

export function PlaybookDetailPage(): JSX.Element {
  const { classes } = useStyles({ dragging: false });
  const navigate = useNavigate();
  const { playbookId, ruleId } = useParams();
  const { updateActiveTabTitle } = useTabContext();
  /**
   * Cache first would show a copy left by the runner drawer or an earlier
   * visit, and the live subscription below only hears changes made while this
   * page is open: suggestions the chat's agent wrote from the document page
   * would not appear until a reload. So the page shows what it has and asks
   * the server once on mount; after that the cache, kept current by the
   * subscription, is enough.
   */
  const { loading, playbook, refetch } = useGetPlaybook(playbookId ?? '', {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !playbookId,
  });
  const playbookTitle = playbook?.title;
  const generationJob = playbook?.generationJob ?? null;
  const isGenerating = getIsGenerationInFlight(generationJob);
  /**
   * The URL follows the page's selection. A click on a rule row pushes, so the
   * back button returns to the rule that was open before. Selection as a side
   * effect (the suggestions stepper, add and delete) replaces: a review of
   * twenty suggestions must not leave twenty entries for the back button to
   * retrace.
   */
  const handleRuleChange = useCallback(
    (nextRuleId: UUID | null, mode: RuleNavigationMode): void => {
      if (!playbookId) return;
      navigate(getRuleLink(playbookId, nextRuleId), { replace: mode === 'replace' });
    },
    [navigate, playbookId]
  );

  usePlaybookGenerationProgress({
    generationJobId: generationJob?.id ?? null,
    isInFlight: isGenerating,
    onGone: refetch,
    playbookId: playbookId ?? null,
  });
  usePlaybookLiveUpdates({ playbookId: playbookId ?? null });

  /**
   * The tab opens with the name the list knew; a rename here, or a tab opened
   * straight from the URL, would otherwise keep a stale or generic title.
   * `updateActiveTabTitle` writes history state every time it is called, so
   * the ref keeps a re-run with the same title (a tab switch) a no-op.
   */
  const appliedTabTitleRef = useRef<string | null>(null);
  useEffect(() => {
    if (typeof playbookTitle !== 'string') return;
    const tabTitle = playbookTitle || 'Untitled playbook';
    if (appliedTabTitleRef.current === tabTitle) return;
    appliedTabTitleRef.current = tabTitle;
    updateActiveTabTitle(tabTitle);
  }, [playbookTitle, updateActiveTabTitle]);

  if (loading && !playbook) return <PlaybookDetailSkeleton />;
  if (!playbook) {
    return (
      <Typography align="center" className={classes.message} variant="h1">
        Playbook not found.
      </Typography>
    );
  }

  return (
    <LoadedPlaybookDetail
      isGenerating={isGenerating}
      playbook={playbook}
      urlRuleId={ruleId ?? null}
      onRuleChange={handleRuleChange}
      onStopped={refetch}
    />
  );
}

/**
 * Says the playbook is still being written, so an author who opens it mid-run
 * knows the categories below are what has arrived so far rather than all there
 * is. The open category shows one rule skeleton; a generic card stands in between
 * categories.
 *
 * Stop ends the run and keeps whatever it has written. The playbook stays as
 * it is at that moment, with the categories that landed and none of the ones
 * that did not.
 */
function GeneratingBanner({
  generationJob,
  onStopped,
}: {
  generationJob: PlaybookGenerationProgress;
  onStopped: () => void;
}): JSX.Element {
  const { classes } = useStyles({ dragging: false });
  const [cancelGeneration, { loading: stopping }] = Apollo.useCancelPlaybookGenerationJobMutation();
  const stopGeneration = useCallback(async () => {
    await cancelGeneration({ variables: { jobId: generationJob.id } });
    onStopped();
  }, [cancelGeneration, generationJob.id, onStopped]);

  return (
    <AlineAlert
      action={
        <AlineButton
          color="primary"
          data-cy={Select.playbooks.stopGenerationButton}
          loading={stopping}
          size="small"
          startIcon={<StopCircle size={16} />}
          variant="outlined"
          onClick={() => void stopGeneration()}
        >
          Stop
        </AlineButton>
      }
      className={classes.generationAlert}
      icon={<PulseIcon variant={PulseIconVariant.LoadingPrimary} />}
      severity="info"
      variant="outlined"
    >
      AI is generating your playbook...
    </AlineAlert>
  );
}

/** Says the run that was writing this playbook stopped, and why. */
function GenerationFailedBanner({ failedReason }: { failedReason: string | null }): JSX.Element {
  const { classes } = useStyles({ dragging: false });

  return (
    <AlineAlert className={classes.generationAlert} severity="error" variant="outlined">
      {failedReason || 'AI generation of this playbook failed.'}
    </AlineAlert>
  );
}

/**
 * Stands in for the title, description and category cards while the playbook
 * loads, so the page keeps its shape instead of appearing all at once. The
 * details column is there but empty: which rule is open is only known once
 * the playbook has arrived, and the column's width must not change then.
 */
function PlaybookDetailSkeleton(): JSX.Element {
  const { classes } = useStyles({ dragging: false });

  return (
    <div className={classes.container}>
      <div className={classes.editorColumn}>
        <div className={classes.skeletonHeader}>
          <div className={classes.skeletonTitleRow}>
            <Skeleton height={24} variant="circular" width={24} />
            <Skeleton height={32} variant="text" width="40%" />
          </div>
          <Skeleton height={20} variant="text" width="60%" />
        </div>
        <div className={classes.skeletonRules}>
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton
              key={index}
              className={classes.skeletonCard}
              height={132}
              variant="rectangular"
            />
          ))}
        </div>
      </div>
      <div className={classes.detailsColumn} />
    </div>
  );
}

/**
 * Lets the user drag the divider to resize the two columns. What is stored
 * is the details column's share of the page, not a pixel width, so the split
 * the user chose (say 60/40) holds when the chat drawer opens or closes or
 * the window changes size, and survives reloads; the rules column takes the
 * rest. A drag clamps so neither column goes under its minimum at the width
 * the page has then; the column's min and max widths hold those minimums
 * when the page later narrows. A double-click on the divider forgets the
 * share and returns to the default split.
 */
function useColumnResize(containerRef: React.RefObject<HTMLDivElement | null>): {
  detailsRatio: number | undefined;
  dividerProps: Pick<
    React.HTMLAttributes<HTMLDivElement>,
    'onDoubleClick' | 'onPointerDown' | 'onPointerMove' | 'onPointerUp' | 'onPointerCancel'
  >;
  dragging: boolean;
} {
  const [storedRatio, setStoredRatio, clearStoredRatio] =
    useLocalStorage<number>(DETAILS_RATIO_STORAGE_KEY);
  const [dragging, setDragging] = useState(false);
  /** Where the pointer went down and how wide the column was then; null between drags. */
  const dragStartRef = useRef<{ pointerX: number; width: number } | null>(null);
  useOverrideGlobalCursor(dragging ? 'col-resize' : null);

  const clampWidth = useCallback(
    (width: number) => {
      const container = containerRef.current;
      const max = container
        ? container.clientWidth - EDITOR_MIN_WIDTH_PX
        : Number.POSITIVE_INFINITY;
      return Math.min(Math.max(width, DETAILS_MIN_WIDTH_PX), Math.max(max, DETAILS_MIN_WIDTH_PX));
    },
    [containerRef]
  );

  const dividerProps = useMemo(
    () => ({
      onDoubleClick: () => clearStoredRatio(),
      onPointerCancel: (event: React.PointerEvent<HTMLDivElement>) => {
        event.currentTarget.releasePointerCapture(event.pointerId);
        dragStartRef.current = null;
        setDragging(false);
      },
      onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => {
        if (event.button !== 0) return;
        const column = event.currentTarget.nextElementSibling;
        if (!(column instanceof HTMLElement)) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        dragStartRef.current = { pointerX: event.clientX, width: column.offsetWidth };
        setDragging(true);
      },
      onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => {
        const start = dragStartRef.current;
        const container = containerRef.current;
        if (!start || !container) return;
        // The divider sits left of the column, so moving left widens it.
        const width = clampWidth(start.width - (event.clientX - start.pointerX));
        setStoredRatio(width / container.clientWidth);
      },
      onPointerUp: (event: React.PointerEvent<HTMLDivElement>) => {
        event.currentTarget.releasePointerCapture(event.pointerId);
        dragStartRef.current = null;
        setDragging(false);
      },
    }),
    [clampWidth, clearStoredRatio, containerRef, setStoredRatio]
  );

  return { detailsRatio: storedRatio, dividerProps, dragging };
}

/**
 * Keeps the current stop on screen in both columns: stepping the bar, or
 * clicking a pill, lands on a rule that may sit far down the page, or a
 * position far down its rule's panel. In the rules column a rule or position
 * stop scrolls to the rule's row (a position's rule is the row that lights
 * up), a category stop to its card, a playbook stop to the top. In the
 * details column a position stop scrolls to its card, and a rule stop or a
 * category add that opens its first rule scrolls to the top, where the title
 * is. Keyed on the stop's key, so a model rebuild around the same stop, or a
 * re-run on a tab switch, scrolls nothing; a click on a rule between two visits to the same
 * stop resets that, so the second visit scrolls again. The panel for a newly
 * opened rule is in the DOM by the time this runs, since it renders in the
 * same commit as the selection change.
 */
function useScrollToCurrentStop({
  currentStop,
  detailsColumnRef,
  editorColumnRef,
}: {
  currentStop: SuggestionStop | null;
  detailsColumnRef: React.RefObject<HTMLDivElement | null>;
  editorColumnRef: React.RefObject<HTMLDivElement | null>;
}): void {
  const scrolledKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const editorColumn = editorColumnRef.current;
    const detailsColumn = detailsColumnRef.current;
    if (!currentStop || !editorColumn || !detailsColumn) {
      scrolledKeyRef.current = null;
      return;
    }
    if (scrolledKeyRef.current === currentStop.key) return;
    scrolledKeyRef.current = currentStop.key;

    if (currentStop.entityType === 'playbook')
      editorColumn.scrollTo({ behavior: 'smooth', top: 0 });
    else
      scrollToSelector(
        editorColumn,
        currentStop.entityType === 'category'
          ? `[data-category-id="${currentStop.entityId}"]`
          : `[data-rule-id="${currentStop.ruleId}"]`
      );

    if (currentStop.entityType === 'position')
      scrollToSelector(detailsColumn, `[data-position-id="${currentStop.entityId}"]`);
    else if (currentStop.ruleId) detailsColumn.scrollTo({ behavior: 'smooth', top: 0 });
  }, [currentStop, detailsColumnRef, editorColumnRef]);
}

function scrollToSelector(column: HTMLElement, selector: string): void {
  const target = column.querySelector(selector);
  if (target) scrollIntoColumn(column, target);
}

/** Breathing room between a scrolled-to row and the column's edge, so it does not sit flush against it. */
const SCROLL_PADDING_PX = 24;

/**
 * The least scroll that brings the target inside the column with padding on
 * the side it came from, like `scrollIntoView` with `block: 'nearest'` but
 * with a margin. A target already inside the padded area moves nothing. One
 * taller than the padded area aligns its top rather than its bottom, so the
 * row's name line is what comes into view.
 */
function scrollIntoColumn(column: HTMLElement, target: Element): void {
  const columnRect = column.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const paddedTop = columnRect.top + SCROLL_PADDING_PX;
  const paddedBottom = columnRect.bottom - SCROLL_PADDING_PX;
  const aboveBy = paddedTop - targetRect.top;
  const belowBy = targetRect.bottom - paddedBottom;
  if (aboveBy > 0) column.scrollBy({ behavior: 'smooth', top: -aboveBy });
  else if (belowBy > 0)
    column.scrollBy({ behavior: 'smooth', top: Math.min(belowBy, targetRect.top - paddedTop) });
}

function getRuleLink(playbookId: string, ruleId: UUID | null): string {
  return ruleId
    ? link.PlaybooksDetailRule({ playbookId, ruleId })
    : link.PlaybooksDetail({ playbookId });
}
